/**
 * DerivedSeasonLabel — the read-only "{year} Season" contextual label for the
 * applicant `/apply` surface (apply-season-auto-derivation spec, task 5.7;
 * design "Components and Interfaces → 7. Optional DerivedSeasonLabel", Option A).
 *
 * The season is auto-derived for an applicant and is NEVER an applicant-facing
 * selection (R5.1, R5.2, R5.4). R5.3 permits — but does not require — showing the
 * Derived_Application_Season to the applicant, provided it is shown as a plain
 * read-only contextual label and NEVER as an editable/selectable control. This
 * component is exactly that: a non-interactive presentational eyebrow rendering
 * "{competitionYear} Season" (e.g. "2026 Season") from the derived season.
 *
 * It is deliberately a plain text element — never a `Select`, `input`, `button`,
 * or any interactive control — so it cannot be mistaken for a season chooser. It
 * is isolated to this single component and referenced from exactly one line in
 * `ApplyRendererSurface`, so switching to Option B (season fully hidden) is a
 * one-line removal.
 *
 * Validates: Requirements 5.3
 */

import * as React from 'react';

export interface DerivedSeasonLabelProps {
  /**
   * The Derived_Application_Season to present, or `null` when derivation is
   * unresolved. When `null` the label renders nothing (the surrounding surface
   * defers to its no-open-window handling).
   */
  season: { competitionYear: number; displayName?: string | null } | null;
}

/**
 * A non-interactive, read-only "{year} Season" label. Renders nothing when no
 * season is derived. This is orientation-only context — it exposes no control by
 * which the applicant could select, change, or confirm a season (R5.1, R5.3).
 */
export function DerivedSeasonLabel({ season }: DerivedSeasonLabelProps): React.ReactElement | null {
  if (!season) return null;

  return (
    <p
      data-testid="derived-season-label"
      className="font-sans text-body-sm font-medium uppercase tracking-wide text-muted-foreground"
    >
      {season.competitionYear} Season
    </p>
  );
}

export default DerivedSeasonLabel;
